// Relatório de CADÊNCIA (/v1/reports/cadence): em que tentativa da cadência
// cada lead está AGORA, se a próxima já venceu e se as anteriores saíram na
// hora marcada. Só gestor — o back devolve 403 para o resto.
//
// Duas leituras, uma dentro da outra: a LISTA (uma linha por lead em cadência)
// e a ESCADA de UM lead (as tentativas com previsto × realizado), buscada só
// quando a linha expande. Carregar a escada de todo mundo seria N×M por nada.
//
// O que NÃO está aqui: a REGRA da cadência (quantas tentativas por desfecho, de
// quanto em quanto tempo). Aquilo é ./queue-config.js — o que se edita em
// Gestão › Cadência. Este módulo só relata o que a regra produziu, e por isso
// não a importa: os tetos chegam prontos em cada item (`maxAttempts`).
import { api, toApiError } from "./client.js";

export type CadenceSituation = "waiting" | "late" | "capped" | "stopped";

export interface CadenceLeadItem {
  campaignLeadId: string;
  leadId: string;
  leadName: string | null;
  leadPhone: string | null;
  sellerId: string | null;
  sellerName: string | null;
  campaignId: string | null;
  // Desfecho técnico da ÚLTIMA tentativa (busy, no_answer, voicemail…).
  outcome: string;
  // Posição da tentativa DENTRO daquele desfecho — não é a enésima ligação do
  // lead. O 2º ocupado é 2 mesmo que o lead já tenha 5 ligações no total.
  attemptNumber: number;
  // Teto do desfecho na regra vigente. Null = desfecho sem regra própria.
  maxAttempts: number | null;
  totalAttempts: number;
  lastAttemptAt: string; // ISO
  nextAttemptAt: string | null; // ISO
  // Retorno marcado pelo VENDEDOR: não é cadência automática.
  isScheduled: boolean;
  situation: CadenceSituation;
  lateMinutes: number | null;
}

export interface CadenceReport {
  items: CadenceLeadItem[];
  total: number;
  situations: CadenceSituation[];
  limit: number;
  offset: number;
}

export interface CadenceAttemptItem {
  id: string;
  callId: string | null;
  outcome: string;
  position: number;
  /** Teto do desfecho DESTA tentativa — a cadência troca junto com o desfecho,
   *  então cada degrau tem a sua régua. Null quando a org não tem regra para
   *  esse desfecho (cai no teto global e inventar um número aqui mentiria). */
  maxAttempts: number | null;
  attemptedAt: string; // ISO
  // Para quando a tentativa estava marcada. Null na 1ª do lead e em tudo que é
  // anterior ao carimbo (migração 0067) — a tela mostra "—", não zero.
  scheduledFor: string | null;
  delayMinutes: number | null;
}

export async function getCadenceReport(params: {
  // Lista separada por vírgula ("waiting,late"). Ausente = só o que está em aberto.
  situations?: string;
  from?: string;
  to?: string;
  outcome?: string;
  sellerId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<CadenceReport> {
  const { data, error } = await api<CadenceReport>("/v1/reports/cadence", { query: params });
  if (error) throw toApiError("GET /v1/reports/cadence", error);
  return data;
}

// A escada de tentativas de UM lead — buscada só quando a linha expande.
export async function getCadenceAttempts(
  campaignLeadId: string,
): Promise<{ items: CadenceAttemptItem[] }> {
  const { data, error } = await api<{ items: CadenceAttemptItem[] }>(
    `/v1/reports/cadence/${encodeURIComponent(campaignLeadId)}/attempts`,
  );
  if (error) throw toApiError("GET /v1/reports/cadence/:id/attempts", error);
  return data;
}
